/**
 * Browser port of tools/migration-v2/src/selection.ts: same flags, same matching, same
 * --percent / --limit / --order / --seed arithmetic and the same messages, so what the
 * simulator picks is what the tool would pick for the same data. Keep the two in step.
 */
import { mulberry32, type SimEmployee } from "./source.js";

export type SelectionOrder = "id" | "hire-date" | "random";

export interface SelectionFilters {
  statuses?:     string[] | undefined;
  roles?:        string[] | undefined;
  types?:        string[] | undefined;
  departments?:  string[] | undefined;
  offices?:      string[] | undefined;
  locations?:    string[] | undefined;
  designations?: string[] | undefined;
  codes?:        string[] | undefined;
  codesFile?:    string | undefined;    // simulator only: the uploaded file the codes came from
  legacyIds?:    number[] | undefined;
  emailDomains?: string[] | undefined;
  hiredFrom?:    string | undefined;
  hiredTo?:      string | undefined;
  percent?:      number | undefined;
  limit?:        number | undefined;
  order?:        SelectionOrder | undefined;
  seed?:         number | undefined;
  withManagers?:    boolean | undefined;
  includeMigrated?: boolean | undefined;
}

export interface Selection {
  matched:         SimEmployee[];
  alreadyMigrated: number;
  picked:          SimEmployee[];
  managersAdded:   number;
  unknownCodes:    string[];
}

export const SELECTION_FLAGS = [
  "--status", "--role", "--type", "--department", "--office", "--location", "--designation",
  "--codes", "--codes-file", "--legacy-ids", "--email-domain", "--hired-from", "--hired-to",
  "--percent", "--limit", "--order", "--seed", "--with-managers", "--include-migrated",
];

export function canonicalCode(code: string): string {
  return code.trim().toUpperCase().replace(/(\D)0+(\d)/g, "$1$2");
}

// --name <value> or --name=<value>
export function arg(argv: string[], name: string): string | null {
  const eq = argv.find((a) => a.startsWith(`${name}=`));
  if (eq) return eq.slice(name.length + 1);
  const i = argv.indexOf(name);
  return i >= 0 && argv[i + 1] && !argv[i + 1]!.startsWith("--") ? argv[i + 1]! : null;
}

function list(value: string | null): string[] | undefined {
  const items = value?.split(",").map((s) => s.trim()).filter(Boolean);
  return items?.length ? items : undefined;
}

function num(value: string | null, name: string, min: number, max: number): number | undefined {
  if (value === null) return undefined;
  const n = Number(value);
  if (!Number.isFinite(n) || n < min || n > max) throw new Error(`${name} must be a number from ${min} to ${max} (got "${value}")`);
  return n;
}

function isoDay(value: string | null, name: string): string | undefined {
  if (value === null) return undefined;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error(`${name} must be YYYY-MM-DD (got "${value}")`);
  return value;
}

export function splitCodes(text: string): string[] {
  return text.split(/[\r\n,;\t]+/).map((s) => s.trim()).filter(Boolean);
}

export function hasSelectionFlags(argv: string[]): boolean {
  return argv.some((a) => SELECTION_FLAGS.some((f) => a === f || a.startsWith(`${f}=`)));
}

/** `readFile` returns an uploaded codes file's text, or null when no file has that name. */
export function filtersFromArgs(argv: string[], readFile: (name: string) => string | null): SelectionFilters {
  const codesFile = arg(argv, "--codes-file");
  let fileCodes: string[] = [];
  if (codesFile) {
    const text = readFile(codesFile);
    if (text === null) throw new Error(`ENOENT: no such file or directory, open '${codesFile}' (upload it under Codes file first)`);
    fileCodes = splitCodes(text);
  }
  const codes = [...(list(arg(argv, "--codes")) ?? []), ...fileCodes];
  const order = arg(argv, "--order");
  if (order && !["id", "hire-date", "random"].includes(order)) throw new Error(`--order must be id, hire-date or random (got "${order}")`);
  const legacyIds = list(arg(argv, "--legacy-ids"))?.map((v) => {
    const n = Number(v);
    if (!Number.isInteger(n)) throw new Error(`--legacy-ids: "${v}" is not a number`);
    return n;
  });

  const f: SelectionFilters = {
    statuses:     list(arg(argv, "--status"))?.map((s) => s.toUpperCase()),
    roles:        list(arg(argv, "--role"))?.map((s) => s.toUpperCase()),
    types:        list(arg(argv, "--type"))?.map((s) => s.toUpperCase()),
    departments:  list(arg(argv, "--department")),
    offices:      list(arg(argv, "--office")),
    locations:    list(arg(argv, "--location"))?.map((s) => s.toUpperCase()),
    designations: list(arg(argv, "--designation")),
    codes:        codes.length ? codes : undefined,
    codesFile:    codesFile ?? undefined,
    legacyIds,
    emailDomains: list(arg(argv, "--email-domain"))?.map((s) => s.toLowerCase().replace(/^@/, "")),
    hiredFrom:    isoDay(arg(argv, "--hired-from"), "--hired-from"),
    hiredTo:      isoDay(arg(argv, "--hired-to"), "--hired-to"),
    percent:      num(arg(argv, "--percent"), "--percent", 0.01, 100),
    limit:        num(arg(argv, "--limit"), "--limit", 1, 1_000_000),
    order:        (order as SelectionOrder | null) ?? undefined,
    seed:         num(arg(argv, "--seed"), "--seed", 0, 2 ** 31),
    withManagers:    argv.includes("--with-managers") || undefined,
    includeMigrated: argv.includes("--include-migrated") || undefined,
  };
  return clean(f);
}

/** The filter panel's view of a command: codes typed on the line, the codes file by name only. */
export function panelFromArgs(argv: string[]): SelectionFilters {
  return filtersFromArgs(argv, () => "");
}

export function clean(f: SelectionFilters): SelectionFilters {
  for (const k of Object.keys(f) as Array<keyof SelectionFilters>) if (f[k] === undefined) delete f[k];
  return f;
}

// ── matching ─────────────────────────────────────────────────────────────────

const lower = (s: string) => s.trim().toLowerCase();

export type ValueCounts = Array<[string, number]>;

export function distinctValues(all: SimEmployee[]) {
  const count = (pick: (m: SimEmployee) => string): ValueCounts => {
    const out = new Map<string, number>();
    for (const m of all) out.set(pick(m), (out.get(pick(m)) ?? 0) + 1);
    return [...out.entries()].sort((a, b) => b[1] - a[1]);
  };
  return {
    statuses:    count((m) => m.status),
    roles:       count((m) => m.role),
    types:       count((m) => m.employeeType),
    departments: count((m) => m.departmentName),
    offices:     count((m) => m.officeId),
    locations:   count((m) => m.workLocation),
  };
}

function checkKnown(label: string, wanted: string[] | undefined, known: ValueCounts, fold: (s: string) => string) {
  if (!wanted) return;
  const have = new Set(known.map(([v]) => fold(v)));
  const bad = wanted.filter((w) => !have.has(fold(w)));
  if (bad.length) throw new Error(`Unknown ${label}: ${bad.join(", ")}\n  Available: ${known.map(([v, n]) => `${v} (${n})`).join(", ")}`);
}

export function validateFilters(f: SelectionFilters, all: SimEmployee[]): void {
  const v = distinctValues(all);
  const upper = (s: string) => s.toUpperCase();
  checkKnown("--status", f.statuses, v.statuses, upper);
  checkKnown("--role", f.roles, v.roles, upper);
  checkKnown("--type", f.types, v.types, upper);
  checkKnown("--location", f.locations, v.locations, upper);
  checkKnown("--department", f.departments, v.departments, lower);
  checkKnown("--office", f.offices, v.offices, lower);
  if (f.hiredFrom && f.hiredTo && f.hiredFrom > f.hiredTo) throw new Error("--hired-from is after --hired-to");
}

function matches(m: SimEmployee, f: SelectionFilters, codes: Set<string> | null): boolean {
  if (f.statuses && !f.statuses.includes(m.status)) return false;
  if (f.roles && !f.roles.includes(m.role)) return false;
  if (f.types && !f.types.includes(m.employeeType)) return false;
  if (f.locations && !f.locations.includes(m.workLocation)) return false;
  if (f.departments && !f.departments.some((d) => lower(d) === lower(m.departmentName))) return false;
  if (f.offices && !f.offices.some((o) => lower(o) === lower(m.officeId))) return false;
  if (f.designations && !f.designations.some((d) => lower(m.designation).includes(lower(d)))) return false;
  if (f.emailDomains && !f.emailDomains.some((d) => m.email.toLowerCase().endsWith(`@${d}`))) return false;
  if (f.legacyIds && !f.legacyIds.includes(m.legacyBayUserId)) return false;
  if (codes && !codes.has(canonicalCode(m.employeeCode)) && !(m.legacyEmpId && codes.has(canonicalCode(m.legacyEmpId)))) return false;
  if (f.hiredFrom && m.hireDate < f.hiredFrom) return false;
  if (f.hiredTo && m.hireDate > f.hiredTo) return false;
  return true;
}

function shuffle<T>(items: T[], seed: number): T[] {
  const rand = mulberry32(seed);
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

export function selectEmployees(all: SimEmployee[], f: SelectionFilters, migrated: Set<number>): Selection {
  const codes = f.codes ? new Set(f.codes.map(canonicalCode)) : null;
  const matched = all.filter((m) => matches(m, f, codes));
  const unknownCodes = f.codes
    ? f.codes.filter((c) => !all.some((m) => canonicalCode(m.employeeCode) === canonicalCode(c) || (m.legacyEmpId && canonicalCode(m.legacyEmpId) === canonicalCode(c))))
    : [];

  const fresh = (m: SimEmployee) => f.includeMigrated || !migrated.has(m.legacyBayUserId);
  let candidates = matched.filter(fresh);
  if (f.order === "hire-date") candidates.sort((a, b) => a.hireDate.localeCompare(b.hireDate) || a.legacyBayUserId - b.legacyBayUserId);
  else if (f.order === "random") candidates = shuffle(candidates, f.seed ?? Date.now());
  else candidates.sort((a, b) => a.legacyBayUserId - b.legacyBayUserId);

  let n = candidates.length;
  if (f.percent !== undefined) n = Math.min(n, Math.ceil((matched.length * f.percent) / 100));
  if (f.limit !== undefined) n = Math.min(n, f.limit);
  const picked = candidates.slice(0, n);

  let managersAdded = 0;
  if (f.withManagers) {
    const byBay = new Map(all.map((m) => [m.legacyBayUserId, m]));
    const inRun = new Set(picked.map((m) => m.legacyBayUserId));
    for (let i = 0; i < picked.length; i++) {
      const mgr = picked[i]!.managerBayId ? byBay.get(picked[i]!.managerBayId!) : undefined;
      if (!mgr || inRun.has(mgr.legacyBayUserId) || !fresh(mgr)) continue;
      inRun.add(mgr.legacyBayUserId);
      picked.push(mgr);
      managersAdded++;
    }
  }

  return {
    matched,
    alreadyMigrated: matched.length - matched.filter((m) => !migrated.has(m.legacyBayUserId)).length,
    picked: picked.sort((a, b) => a.legacyBayUserId - b.legacyBayUserId),
    managersAdded,
    unknownCodes,
  };
}

export function describeFilters(f: SelectionFilters): string {
  const parts: string[] = [];
  const add = (label: string, v: unknown) => { if (v !== undefined) parts.push(`${label}=${Array.isArray(v) ? v.join(",") : v}`); };
  add("status", f.statuses); add("role", f.roles); add("type", f.types); add("department", f.departments);
  add("office", f.offices); add("location", f.locations); add("designation", f.designations);
  add("codes", f.codes && (f.codes.length > 10 ? `${f.codes.slice(0, 10).join(",")}…(${f.codes.length})` : f.codes));
  add("legacy-ids", f.legacyIds); add("email-domain", f.emailDomains);
  add("hired-from", f.hiredFrom); add("hired-to", f.hiredTo);
  add("percent", f.percent); add("limit", f.limit); add("order", f.order); add("seed", f.seed);
  if (f.withManagers) parts.push("with-managers");
  if (f.includeMigrated) parts.push("include-migrated");
  return parts.length ? parts.join("  ") : "everyone";
}

export function selectionLines(sel: Selection, f: SelectionFilters, maxRows = 25): string[] {
  const out = [
    "",
    "── Selection ─────────────────────────────────────",
    `  filters:          ${describeFilters(f)}`,
    `  matching:         ${sel.matched.length}`,
    `  already migrated: ${sel.alreadyMigrated}${f.includeMigrated ? " (re-loaded: --include-migrated)" : " (left out)"}`,
    `  picked this run:  ${sel.picked.length}${sel.managersAdded ? ` (incl. ${sel.managersAdded} managers)` : ""}`,
  ];
  if (sel.unknownCodes.length) out.push(`  codes not found:  ${sel.unknownCodes.join(", ")}`);
  for (const m of sel.picked.slice(0, maxRows)) {
    out.push(`    ${m.employeeCode.padEnd(10)} ${`${m.firstName} ${m.lastName}`.padEnd(28).slice(0, 28)} ${m.status.padEnd(13)} ${m.role.padEnd(14)} ${m.departmentName.slice(0, 22).padEnd(22)} ${m.hireDate}`);
  }
  if (sel.picked.length > maxRows) out.push(`    … and ${sel.picked.length - maxRows} more`);
  return out;
}

// ── filters → command line (what the filter panel writes into the command box) ──────────

const quote = (v: string) => (/[\s"]/.test(v) ? `"${v.replace(/"/g, '\\"')}"` : v);

export function filtersToArgs(f: SelectionFilters): string[] {
  const out: string[] = [];
  const add = (flag: string, v: string | number | undefined | Array<string | number>) => {
    if (v === undefined || (Array.isArray(v) && !v.length) || v === "") return;
    out.push(`${flag}=${quote(Array.isArray(v) ? v.join(",") : String(v))}`);
  };
  add("--status", f.statuses); add("--role", f.roles); add("--type", f.types);
  add("--department", f.departments); add("--office", f.offices); add("--location", f.locations);
  add("--designation", f.designations);
  add("--codes", f.codes);   // typed codes only: panelFromArgs keeps the file's codes out
  add("--codes-file", f.codesFile);
  add("--legacy-ids", f.legacyIds); add("--email-domain", f.emailDomains);
  add("--hired-from", f.hiredFrom); add("--hired-to", f.hiredTo);
  add("--percent", f.percent); add("--limit", f.limit);
  if (f.order && f.order !== "id") add("--order", f.order);
  if (f.order === "random") add("--seed", f.seed);
  if (f.withManagers) out.push("--with-managers");
  if (f.includeMigrated) out.push("--include-migrated");
  return out;
}
